import { useEffect, useState } from 'react';
import {
  Bus,
  CheckCircle2,
  DollarSign,
  GraduationCap,
  Rocket,
  Ticket,
  Volleyball,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  AppHeader,
  AppScreen,
  BalanceCard,
  ExpenseRow,
  Fab,
  SectionHeaderRow,
  StatusBar,
  TabBar,
} from './kit';
import { PhoneFrame } from './PhoneFrame';
import { prefersReducedMotion, useInView } from './useInView';
import './ExpensesMotionDemo.css';

interface Transaction {
  icon: LucideIcon;
  iconColor: string;
  title: string;
  sub: string;
  amount: string;
}

const TRANSACTIONS: ReadonlyArray<Transaction> = [
  {
    icon: Ticket,
    iconColor: '#F5A623',
    title: 'Movie tickets',
    sub: 'Mar 7 · Entertainment',
    amount: '$24.98',
  },
  {
    icon: Rocket,
    iconColor: '#3B82F6',
    title: 'SAT classes',
    sub: 'Mar 6 · School',
    amount: '$89.00',
  },
  {
    icon: Bus,
    iconColor: '#018B9C',
    title: 'Bus ticket for game',
    sub: 'Mar 6 · Transportation',
    amount: '$12.74',
  },
  {
    icon: Volleyball,
    iconColor: '#22A06B',
    title: 'Soccer Jersey',
    sub: 'Mar 6 · Activities',
    amount: '$39.98',
  },
  {
    icon: DollarSign,
    iconColor: '#3B82F6',
    title: 'Payment',
    sub: 'Mar 4 · Venmo',
    amount: '$10.00',
  },
  {
    icon: GraduationCap,
    iconColor: '#3B82F6',
    title: 'School supplies',
    sub: 'Mar 4 · School',
    amount: '$124.60',
  },
];

const TOTAL_CENTS = 9236;
const COUNT_DELAY_MS = 150;
const COUNT_DURATION_MS = 900;
const ROW_STAGGER_MS = 90;
const PAID_BACK_DELAY_MS = 1400;

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function ExpensesMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.25 });
  const [reduced] = useState(prefersReducedMotion);
  const [amount, setAmount] = useState(() =>
    formatCents(reduced ? TOTAL_CENTS : 0),
  );
  const [paidBack, setPaidBack] = useState(reduced);

  useEffect(() => {
    if (reduced) {
      setAmount(formatCents(TOTAL_CENTS));
      setPaidBack(true);
      return;
    }

    if (!seen) return;

    let frame = 0;
    let startTimer = 0;

    startTimer = window.setTimeout(() => {
      const startedAt = performance.now();

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / COUNT_DURATION_MS);
        const eased = 1 - Math.pow(1 - progress, 3);
        setAmount(formatCents(Math.round(TOTAL_CENTS * eased)));
        if (progress < 1) {
          frame = window.requestAnimationFrame(tick);
        }
      };

      frame = window.requestAnimationFrame(tick);
    }, COUNT_DELAY_MS);

    const paidTimer = window.setTimeout(
      () => setPaidBack(true),
      PAID_BACK_DELAY_MS,
    );

    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(paidTimer);
      window.cancelAnimationFrame(frame);
    };
  }, [seen, reduced]);

  const stageClass = reduced ? 'is-static' : seen ? 'is-playing' : '';

  return (
    <div ref={ref} className={`expenses-demo${stageClass ? ` ${stageClass}` : ''}`}>
      <PhoneFrame size='md'>
        <AppScreen size='md'>
          <StatusBar />
          <AppHeader variant='brand' />

          <div className='mk-body expenses-demo__body'>
            <BalanceCard label='You owe your coparent' amount={amount} />

            <SectionHeaderRow title='All transactions' />

            <div className='mk-stack'>
              {TRANSACTIONS.map((transaction, index) => {
                const isMovieTickets = index === 0;
                const showPaidBack = isMovieTickets && paidBack;
                const delay =
                  (TRANSACTIONS.length - 1 - index) * ROW_STAGGER_MS;

                return (
                  <div
                    key={transaction.title}
                    className={`expenses-demo__row${
                      showPaidBack ? ' is-paid' : ''
                    }`}
                    style={{ animationDelay: `${delay}ms` }}
                  >
                    <ExpenseRow
                      icon={transaction.icon}
                      iconColor={transaction.iconColor}
                      title={transaction.title}
                      sub={transaction.sub}
                      amount={transaction.amount}
                      split={showPaidBack ? undefined : '50% - 50%'}
                      footer={
                        showPaidBack ? (
                          <span className='expenses-demo__paid'>
                            <CheckCircle2 size={13} strokeWidth={2.4} />
                            Paid Back
                          </span>
                        ) : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <Fab />
          <TabBar active='home' />
        </AppScreen>
      </PhoneFrame>
    </div>
  );
}
